import { useUserInfo } from "@/api/user";
import { useAdminPanelNavigationItems } from "@/components/AdminPanel/AdminPanel";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { SettingsInDialogProvider } from "@/context/SettingsInDialogContext";
import { SettingsInitialNavigationProvider } from "@/context/SettingsInitialNavigationContext";
import { cn } from "@/utils";
import {
  isSettingsRootPath,
  resolveSettingsMenuItemPath,
} from "@/utils/settingsNavigation";
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

interface SettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialPathname?: string;
}

const DESCRIPTIONS: Record<string, string> = {
  Settings: "Manage your account settings and preferences",
  Account: "Manage your account settings and preferences",
  Notifications: "Manage how and where you receive notifications",
  Calendar: "Connect your calendar to let Cohesive join your meetings",
  Connections: "Configure your external service connectors",
  Connectors: "Configure your external service connectors",
  Skills: "Manage your agent's skills and knowledge",
  Microphone: "Configure your microphone and audio recording preferences",
  Organization: "Manage your organization settings and branding",
  Members: "Manage organization members and their permissions",
  Groups: "Create and manage user groups",
  Assistants: "Configure organization-wide AI agents",
  "Single sign-on": "Configure SSO for your organization",
  "External apps": "Manage integrations with external applications",
  "Usage & Billing": "Monitor agent usage and manage billing settings",
};

type SettingsItem = ReturnType<
  typeof useAdminPanelNavigationItems
>["menuItems"][number];

/** True when the event is dismissing a portaled menu/select (or lands on one). */
function isPortaledOverlayDismiss(target: EventTarget | null): boolean {
  const openPortaledLayer = !!document.querySelector(
    '[data-radix-menu-content][data-state="open"], [data-radix-select-content][data-state="open"]',
  );

  if (
    target instanceof Element &&
    target.closest(
      "[data-radix-popper-content-wrapper], [data-radix-menu-content], [data-radix-select-content], [data-slot='dropdown-menu-content'], [data-slot='select-content']",
    )
  ) {
    return true;
  }

  // Click-away to dismiss an open menu hits a layer outside DialogContent and
  // would otherwise close Settings (modal={false}).
  return openPortaledLayer;
}

export function SettingsDialog({
  open,
  onOpenChange,
  initialPathname,
}: SettingsDialogProps) {
  const { menuItems, workspaceItems, allMenuItems } =
    useAdminPanelNavigationItems();
  const { data: userInfo } = useUserInfo();
  const [selectedItem, setSelectedItem] = useState<SettingsItem | null>(null);
  const [contentKey, setContentKey] = useState(0);
  const [initialNavigationConsumed, setInitialNavigationConsumed] =
    useState(false);

  const activeInitialPathname = initialNavigationConsumed
    ? undefined
    : initialPathname;

  // Sync selection when the dialog opens, when menus finish loading, or when the
  // current selection disappears. Keep the user's choice across re-renders even if
  // useAdminPanelNavigationItems returns new array references every render.
  useEffect(() => {
    if (!open) {
      setSelectedItem(null);
      setInitialNavigationConsumed(false);
      return;
    }

    setSelectedItem((prev) => {
      if (prev && allMenuItems.some((i) => i.path === prev.path)) {
        return prev;
      }
      if (initialPathname) {
        if (isSettingsRootPath(initialPathname)) {
          return menuItems[0] ?? workspaceItems[0] ?? null;
        }

        const menuItemPath = resolveSettingsMenuItemPath(
          initialPathname,
          allMenuItems,
        );
        if (menuItemPath) {
          return (
            allMenuItems.find((i) => i.path === menuItemPath) ??
            menuItems[0] ??
            workspaceItems[0] ??
            null
          );
        }

        return null;
      }
      return menuItems[0] ?? workspaceItems[0] ?? null;
    });
  }, [open, initialPathname, menuItems, workspaceItems, allMenuItems]);

  const SelectedComponent = selectedItem?.component;

  const renderNavSection = (label: string, items: SettingsItem[]) => {
    if (items.length === 0) return null;
    return (
      <div className="mb-5">
        <div className="flex items-center gap-2 px-3 mb-1.5">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/60 select-none">
            {label}
          </span>
        </div>
        <ul className="space-y-0.5">
          {items.map((item) => {
            const isActive = selectedItem?.path === item.path;
            return (
              <li key={item.path}>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedItem(item);
                    setContentKey((k) => k + 1);
                    setInitialNavigationConsumed(true);
                  }}
                  className={cn(
                    "w-full flex items-center gap-2.5 px-3 py-1.5 rounded-md text-sm transition-colors duration-100 text-left",
                    isActive
                      ? "bg-accent text-accent-foreground font-medium"
                      : "text-muted-foreground hover:bg-accent/50 hover:text-foreground",
                  )}
                >
                  <span
                    className={cn(
                      "shrink-0 [&_svg]:size-[15px]",
                      isActive ? "text-foreground" : "text-muted-foreground/70",
                    )}
                  >
                    {item.icon}
                  </span>
                  {item.label}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  return (
    <>
      {open &&
        createPortal(
          <div className="fixed inset-0 bg-n-95/55 dark:bg-n-00/55 animate-in fade-in-0 duration-200" />,
          document.body,
        )}
      <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
        <DialogContent
          className="!max-w-[min(90vw,1300px)] w-[min(90vw,1300px)] !max-h-[min(88vh,1250px)] h-[min(88vh,1250px)] flex flex-col p-0 gap-0 overflow-hidden z-50"
          hideCloseButton
          onInteractOutside={(event) => {
            // Dropdown/Select default modal layers portal outside DialogContent.
            // Dismissing them must not close Settings.
            if (isPortaledOverlayDismiss(event.target)) {
              event.preventDefault();
            }
          }}
          onPointerDownOutside={(event) => {
            if (isPortaledOverlayDismiss(event.target)) {
              event.preventDefault();
            }
          }}
        >
          {/* Visually hidden title for accessibility */}
          <DialogTitle className="sr-only">Settings</DialogTitle>

          <div className="flex flex-1 min-h-0 overflow-hidden">
            {/* Left sidebar */}
            <aside className="w-56 shrink-0 flex flex-col border-r bg-muted/20 overflow-y-auto">
              <div className="px-4 pt-5 pb-3 shrink-0">
                <h2 className="text-base font-semibold text-foreground tracking-tight">
                  Settings
                </h2>
                <p className="text-xs text-muted-foreground mt-0.5 truncate">
                  {userInfo?.user?.email ?? ""}
                </p>
              </div>

              <nav className="flex-1 px-2 pb-4">
                {renderNavSection("Account", menuItems)}
                {workspaceItems.length > 0 &&
                  renderNavSection("Organization", workspaceItems)}
              </nav>
            </aside>

            {/* Right content panel */}
            <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
              {/* Panel header */}
              <div className="flex items-center justify-between px-8 pt-6 pb-0 shrink-0">
                <div>
                  <h1 className="text-xl font-semibold text-foreground leading-tight">
                    {selectedItem?.label ?? "Settings"}
                  </h1>
                  {selectedItem && (
                    <p className="text-sm text-muted-foreground mt-0.5">
                      {DESCRIPTIONS[selectedItem.label] ??
                        "Configure settings for this section"}
                    </p>
                  )}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground"
                  onClick={() => onOpenChange(false)}
                  aria-label="Close settings"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {/* Divider */}
              <div className="mx-8 mt-4 mb-0 border-t" />

              {/* Content area */}
              <div className="flex-1 overflow-y-auto px-8 py-6 min-h-0">
                {SelectedComponent ? (
                  <SettingsInDialogProvider value={true}>
                    <SettingsInitialNavigationProvider
                      value={{ pathname: activeInitialPathname }}
                    >
                      <SelectedComponent key={contentKey} />
                    </SettingsInitialNavigationProvider>
                  </SettingsInDialogProvider>
                ) : (
                  <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
                    Select a setting from the sidebar
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
